import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import PermissionResourceSchema from "permissions/resources/_schemas/permission-resource-schema";
import CalendarEventAccessSchema, { CalendarEventAccessJSON, CalendarEventAccessParams } from "permissions/resources/calendar/_schemas/calendar-event-access-schema";
import DocumentAccessSchema, { DocumentAccessJSON, DocumentAccessParams } from "permissions/resources/documents/_schemas/document-access-schema";
import DocumentContentsAccessSchema, { DocumentContentsAccessJSON, DocumentContentsAccessParams } from "permissions/resources/documents/_schemas/document-contents-access-schema";
import GeneralDocumentAccessSchema, { GeneralDocumentAccessJSON, GeneralDocumentAccessParams } from "permissions/resources/documents/_schemas/general-document-access-schema";
import KnowledgeBaseAccessSchema, { KnowledgeBaseAccessJSON, KnowledgeBaseAccessParams } from "permissions/resources/knowledge-base/knowledge-base-access-schema";
import EntityAgreementAccessSchema, { EntityAgreementAccessJSON, EntityAgreementAccessParams } from "permissions/resources/legal-entities/_schemas/entity-agreement-access-schema";
import LegalEntitiesPlatformAccessSchema, {
  LegalEntitiesPlatformAccessJSON,
  LegalEntitiesPlatformAccessParams,
} from "permissions/resources/legal-entities/_schemas/legal-entities-platform-access-schema";
import LegalEntityAccessSchema, {
  LegalEntityAccessJSON,
  LegalEntityAccessParams,
} from "permissions/resources/legal-entities/_schemas/legal-entity-access-schema";
import MarketplaceAccessSchema, { MarketplaceAccessJSON, MarketplaceAccessParams } from "permissions/resources/marketplace/_schema/marketplace-access-schema";
import MarketplaceSearchAccessSchema, { MarketplaceSearchAccessJSON, MarketplaceSearchAccessParams } from "permissions/resources/marketplace/_schema/marketplace-search-access-schema";
import MarketplaceDirectoryAccessSchema, { MarketplaceDirectoryAccessJSON, MarketplaceDirectoryAccessParams } from "permissions/resources/marketplace/_schema/marketplace-directory-access-schema";
import CommunicationsAccessSchema, { CommunicationsAccessJSON, CommunicationsAccessParams } from "permissions/resources/messaging/_schemas/communications-access-schema";
import ForumAccessSchema, { ForumAccessJSON, ForumAccessParams } from "permissions/resources/messaging/_schemas/forum-access-schema";
import NotificationsAccessSchema, { NotificationsAccessJSON, NotificationsAccessParams } from "permissions/resources/notifications/notifications-permissions-resources-schema";
import { AnyResourceJSON, AnyResourceParams, ResourceType } from "permissions/resources/permission-resource-types";
import LoginAccessSchema, {
  LoginAccessJSON,
  LoginAccessParams,
} from "permissions/resources/users/_schemas/login-access-schema";
import UserNetworkingAccessSchema, { UserNetworkingAccessJSON, UserNetworkingAccessParams } from "permissions/resources/users/_schemas/user-networking-access-schema";
import ContractsAccessSchema, { ContractsAccessJSON, ContractsAccessParams } from "permissions/resources/work/_schemas/contracts-access-schema";
import CurrencyFieldsAccessSchema, { CurrencyFieldsAccessJSON, CurrencyFieldsAccessParams } from "permissions/resources/work/_schemas/currency-fields-access-schema";
import DiscountsAccessSchema, { DiscountsAccessJSON, DiscountsAccessParams } from "permissions/resources/work/_schemas/discounts-access-schema";
import FeeScheduleTemplateAccessSchema, { FeeScheduleTemplateAccessJSON, FeeScheduleTemplateAccessParams } from "permissions/resources/work/_schemas/fee-schedule-template-access-schema";
import FeeSchedulesAccessSchema, { FeeSchedulesAccessJSON, FeeSchedulesAccessParams } from "permissions/resources/work/_schemas/fee-schedules-access-schema";
import InvoicesAccessSchema, { InvoicesAccessJSON, InvoicesAccessParams } from "permissions/resources/work/_schemas/invoices-access-schema";
import ProposalAccessSchema, { ProposalAccessJSON, ProposalAccessParams } from "permissions/resources/work/_schemas/proposal-access-schema";
import ProposalsAccessSchema, { ProposalsAccessJSON, ProposalsAccessParams } from "permissions/resources/work/_schemas/proposals-access-schema";
import TeamTemplateAccessSchema, { TeamTemplateAccessJSON, TeamTemplateAccessParams } from "permissions/resources/work/_schemas/team-template-access-schema";
import TeamsAccessSchema, { TeamsAccessJSON, TeamsAccessParams } from "permissions/resources/work/_schemas/teams-access-schema";
import WorkAgreementAccessSchema, { WorkAgreementAccessJSON, WorkAgreementAccessParams } from "permissions/resources/work/_schemas/work-agreement-access-schema";
import WorkAgreementsAccessSchema, { WorkAgreementsAccessJSON, WorkAgreementsAccessParams } from "permissions/resources/work/_schemas/work-agreements-access-schema";
import { z } from "zod";

/**
 * The template a grant came from, when a template application created or raised it (#6828). A
 * deleted template keeps its name; its row loses the link.
 */
const TemplateSourceJSONZ = z.object({
  templateId: z.uuid(),
  name: z.string(),
  isDeleted: z.boolean(),
});

const TemplateSourceZ = z.object({
  templateId: z.instanceof(Guid),
  name: z.string(),
  isDeleted: z.boolean(),
});

export type PermissionTemplateSource = z.infer<typeof TemplateSourceZ>;

export type PermissionJSON = z.infer<typeof PermissionSchema.PermissionJSONZ>;
export type PermissionParams = z.infer<typeof PermissionSchema.PermissionZ>;

export default class PermissionSchema {
  public static readonly PermissionJSONZ = z.object({
    id: z.uuid().nullish(),
    resource: GeneralDocumentAccessSchema.GeneralDocumentAccessJSONZ.or(
      DocumentContentsAccessSchema.DocumentContentsAccessJSONZ
    )
      .or(CalendarEventAccessSchema.CalendarEventAccessJSONZ)
      .or(DocumentAccessSchema.DocumentAccessJSONZ)
      .or(KnowledgeBaseAccessSchema.KnowledgeBaseAccessJSONZ)
      .or(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessJSONZ)
      .or(EntityAgreementAccessSchema.EntityAgreementAccessJSONZ)
      .or(LegalEntityAccessSchema.LegalEntityAccessJSONZ)
      .or(MarketplaceAccessSchema.MarketplaceAccessJSONZ)
      .or(MarketplaceSearchAccessSchema.MarketplaceSearchAccessJSONZ)
      .or(MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessJSONZ)
      .or(CommunicationsAccessSchema.CommunicationsAccessJSONZ)
      .or(ForumAccessSchema.ForumAccessJSONZ)
      .or(NotificationsAccessSchema.NotificationsAccessJSONZ)
      .or(LoginAccessSchema.LoginAccessJSONZ)
      .or(UserNetworkingAccessSchema.UserNetworkingAccessJSONZ)
      .or(WorkAgreementsAccessSchema.WorkAgreementsAccessJSONZ) //
      .or(ProposalsAccessSchema.ProposalsAccessJSONZ)
      .or(TeamsAccessSchema.TeamsAccessJSONZ)
      .or(ContractsAccessSchema.ContractsAccessJSONZ)
      .or(FeeSchedulesAccessSchema.FeeSchedulesAccessJSONZ)
      .or(CurrencyFieldsAccessSchema.CurrencyFieldsAccessJSONZ)
      .or(DiscountsAccessSchema.DiscountsAccessJSONZ)
      .or(InvoicesAccessSchema.InvoicesAccessJSONZ)
      .or(ProposalAccessSchema.ProposalAccessJSONZ)
      .or(WorkAgreementAccessSchema.WorkAgreementAccessJSONZ)
      .or(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessJSONZ)
      .or(TeamTemplateAccessSchema.TeamTemplateAccessJSONZ),
    entityId: z.uuid().or(z.literal(Guid.EMPTY.value)),
    grantorUserId: z.uuid().or(z.literal(Guid.EMPTY.value)),
    granteeUserId: z.uuid(),
    disabledDate: z.iso.datetime({ offset: true }).nullish(),
    /**
     * Who removed the grant (#6824): null on a live grant and on one the system removed. Missing from
     * a server from before #6825, which does not say.
     */
    disabledByUserId: z.uuid().nullish(),
    createdDate: z.iso.datetime({ offset: true }).nullish(),
    get parentPermissions() {
      return z.array(PermissionSchema.PermissionJSONZ).nullish();
    },
    note: z.string().nullish(),
    version: z.number().int().nonnegative().optional(),
    fromTemplate: TemplateSourceJSONZ.nullish(),
  });

  public static readonly PermissionZ = z.object({
    id: z.instanceof(Guid).optional(),
    resource: GeneralDocumentAccessSchema.GeneralDocumentAccessZ.or(
      DocumentContentsAccessSchema.DocumentContentsAccessZ
    )
      .or(CalendarEventAccessSchema.CalendarEventAccessZ)
      .or(DocumentAccessSchema.DocumentAccessZ)
      .or(KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ)
      .or(LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ)
      .or(EntityAgreementAccessSchema.EntityAgreementAccessZ)
      .or(LegalEntityAccessSchema.LegalEntityAccessZ)
      .or(MarketplaceAccessSchema.MarketplaceAccessZ)
      .or(MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ)
      .or(MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessZ)
      .or(CommunicationsAccessSchema.CommunicationsAccessZ)
      .or(ForumAccessSchema.ForumAccessZ)
      .or(NotificationsAccessSchema.NotificationsAccessZ)
      .or(LoginAccessSchema.LoginAccessZ)
      .or(UserNetworkingAccessSchema.UserNetworkingAccessZ)
      .or(WorkAgreementsAccessSchema.WorkAgreementsAccessZ)
      .or(ProposalsAccessSchema.ProposalsAccessZ)
      .or(TeamsAccessSchema.TeamsAccessZ)
      .or(ContractsAccessSchema.ContractsAccessZ)
      .or(FeeSchedulesAccessSchema.FeeSchedulesAccessZ)
      .or(CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ)
      .or(DiscountsAccessSchema.DiscountsAccessZ)
      .or(InvoicesAccessSchema.InvoicesAccessZ)
      .or(ProposalAccessSchema.ProposalAccessZ)
      .or(WorkAgreementAccessSchema.WorkAgreementAccessZ)
      .or(FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ)
      .or(TeamTemplateAccessSchema.TeamTemplateAccessZ),
    entityId: z.instanceof(Guid),
    grantorUserId: z.instanceof(Guid),
    granteeUserId: z.instanceof(Guid),
    disabledDate: DateSchema.type.optional(),
    disabledByUserId: z.instanceof(Guid).nullable().optional(),
    createdDate: DateSchema.type.optional(),
    get parentPermissions() {
      return z.array(PermissionSchema.PermissionZ).optional();
    },
    note: z.string().optional(),
    version: z.number().int().nonnegative().optional(),
    fromTemplate: TemplateSourceZ.optional(),
  });

  public static validate(values: unknown): PermissionParams {
    return parseOrThrow(PermissionSchema.PermissionZ, values, {
      logError: true,
      schemaName: "PermissionParams",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): PermissionJSON {
    return parseOrThrow(PermissionSchema.PermissionJSONZ, values, {
      logError: true,
      schemaName: "PermissionJSON",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is PermissionParams {
    try {
      PermissionSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is PermissionJSON {
    try {
      PermissionSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: PermissionJSON): PermissionParams {
    const parsed = parseOrThrow(PermissionSchema.PermissionJSONZ, data, {
      logError: true,
      schemaName: "PermissionJSON",
      operation: "deserialize",
      payloadSample: data,
    });

    return PermissionSchema.codec.decode(parsed);
  }

  public static serialize(notification: PermissionParams): PermissionJSON {
    const parsed = PermissionSchema.validate(notification);
    return PermissionSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(PermissionSchema.PermissionJSONZ, PermissionSchema.PermissionZ, {
    decode: (data) => {
      const resourceJson = deserializeResource(data.resource) as
        | z.infer<typeof GeneralDocumentAccessSchema.GeneralDocumentAccessZ>
        | z.infer<typeof DocumentContentsAccessSchema.DocumentContentsAccessZ>
        | z.infer<typeof CalendarEventAccessSchema.CalendarEventAccessZ>
        | z.infer<typeof DocumentAccessSchema.DocumentAccessZ>
        | z.infer<typeof KnowledgeBaseAccessSchema.KnowledgeBaseAccessZ>
        | z.infer<typeof LegalEntitiesPlatformAccessSchema.LegalEntitiesPlatformAccessZ>
        | z.infer<typeof EntityAgreementAccessSchema.EntityAgreementAccessZ>
        | z.infer<typeof LegalEntityAccessSchema.LegalEntityAccessZ>
        | z.infer<typeof MarketplaceAccessSchema.MarketplaceAccessZ>
        | z.infer<typeof MarketplaceSearchAccessSchema.MarketplaceSearchAccessZ>
        | z.infer<typeof MarketplaceDirectoryAccessSchema.MarketplaceDirectoryAccessZ>
        | z.infer<typeof CommunicationsAccessSchema.CommunicationsAccessZ>
        | z.infer<typeof ForumAccessSchema.ForumAccessZ>
        | z.infer<typeof NotificationsAccessSchema.NotificationsAccessZ>
        | z.infer<typeof LoginAccessSchema.LoginAccessZ>
        | z.infer<typeof UserNetworkingAccessSchema.UserNetworkingAccessZ>
        | z.infer<typeof WorkAgreementsAccessSchema.WorkAgreementsAccessZ>
        | z.infer<typeof ProposalsAccessSchema.ProposalsAccessZ>
        | z.infer<typeof TeamsAccessSchema.TeamsAccessZ>
        | z.infer<typeof ContractsAccessSchema.ContractsAccessZ>
        | z.infer<typeof FeeSchedulesAccessSchema.FeeSchedulesAccessZ>
        | z.infer<typeof CurrencyFieldsAccessSchema.CurrencyFieldsAccessZ>
        | z.infer<typeof DiscountsAccessSchema.DiscountsAccessZ>
        | z.infer<typeof InvoicesAccessSchema.InvoicesAccessZ>
        | z.infer<typeof ProposalAccessSchema.ProposalAccessZ>
        | z.infer<typeof WorkAgreementAccessSchema.WorkAgreementAccessZ>
        | z.infer<typeof FeeScheduleTemplateAccessSchema.FeeScheduleTemplateAccessZ>
        | z.infer<typeof TeamTemplateAccessSchema.TeamTemplateAccessZ>;

      return {
        id: Guid.fromJSON(data.id) ?? undefined,
        resource: resourceJson,
        entityId: Guid.fromJSON(data.entityId, { allowEmpty: true })!,
        grantorUserId: Guid.fromJSON(data.grantorUserId, { allowEmpty: true })!,
        granteeUserId: Guid.fromJSON(data.granteeUserId)!,
        disabledDate: AHDate.fromJSON(data.disabledDate) ?? undefined,
        disabledByUserId: data.disabledByUserId === null ? null : (Guid.fromJSON(data.disabledByUserId) ?? undefined),
        createdDate: AHDate.fromJSON(data.createdDate) ?? undefined,
        parentPermissions: data.parentPermissions
          ? data.parentPermissions.map((p) => PermissionSchema.deserialize(p))
          : undefined,
        note: data.note ?? undefined,
        version: data.version ?? 0,
        fromTemplate: data.fromTemplate
          ? { ...data.fromTemplate, templateId: Guid.fromJSON(data.fromTemplate.templateId)! }
          : undefined,
      };
    },
    encode: (permission) => ({
      id: permission.id ? permission.id.toJSON() : undefined,
      resource: serializeResource(permission.resource) as PermissionJSON["resource"],
      entityId: permission.entityId.toJSON(),
      grantorUserId: permission.grantorUserId.toJSON(),
      granteeUserId: permission.granteeUserId.toJSON(),
      disabledDate: permission.disabledDate?.toJSON(),
      disabledByUserId: permission.disabledByUserId === null ? null : permission.disabledByUserId?.toJSON(),
      createdDate: permission.createdDate?.toJSON(),
      parentPermissions: permission.parentPermissions
        ? permission.parentPermissions.map((p) => PermissionSchema.serialize(p))
        : undefined,
      note: permission.note,
      version: permission.version,
      fromTemplate: permission.fromTemplate
        ? { ...permission.fromTemplate, templateId: permission.fromTemplate.templateId.toJSON() }
        : undefined,
    }),
  });
}


export function deserializeResource(json: AnyResourceJSON): AnyResourceParams {
  switch (json.resourceType) {
    case ResourceType.CalendarEventAccess:
      return CalendarEventAccessSchema.deserialize(json as CalendarEventAccessJSON);

    case ResourceType.GeneralDocumentAccess:
      return GeneralDocumentAccessSchema.deserialize(json as GeneralDocumentAccessJSON);

    case ResourceType.DocumentContentsAccess:
      return DocumentContentsAccessSchema.deserialize(json as DocumentContentsAccessJSON);

    case ResourceType.DocumentAccess:
      return DocumentAccessSchema.deserialize(json as DocumentAccessJSON);

    case ResourceType.LegalEntitiesPlatformAccess:
      return LegalEntitiesPlatformAccessSchema.deserialize(json as LegalEntitiesPlatformAccessJSON);

    case ResourceType.EntityAgreementAccess:
      return EntityAgreementAccessSchema.deserialize(json as EntityAgreementAccessJSON);

    case ResourceType.LegalEntityAccess:
      return LegalEntityAccessSchema.deserialize(json as LegalEntityAccessJSON);

    case ResourceType.CommunicationsAccess:
      return CommunicationsAccessSchema.deserialize(json as CommunicationsAccessJSON);

    case ResourceType.ForumAccess:
      return ForumAccessSchema.deserialize(json as ForumAccessJSON);

    case ResourceType.MarketplaceAccess:
      return MarketplaceAccessSchema.deserialize(json as MarketplaceAccessJSON);

    case ResourceType.MarketplaceSearchAccess:
      return MarketplaceSearchAccessSchema.deserialize(json as MarketplaceSearchAccessJSON);

    case ResourceType.MarketplaceDirectoryAccess:
      return MarketplaceDirectoryAccessSchema.deserialize(json as MarketplaceDirectoryAccessJSON);

    case ResourceType.NotificationsAccess:
      return NotificationsAccessSchema.deserialize(json as NotificationsAccessJSON);

    case ResourceType.LoginAccess:
      return LoginAccessSchema.deserialize(json as LoginAccessJSON);

    case ResourceType.UserNetworkingAccess:
      return UserNetworkingAccessSchema.deserialize(json as UserNetworkingAccessJSON);

    case ResourceType.WorkAgreementsAccess:
      return WorkAgreementsAccessSchema.deserialize(json as WorkAgreementsAccessJSON);

    case ResourceType.ProposalsAccess:
      return ProposalsAccessSchema.deserialize(json as ProposalsAccessJSON);

    case ResourceType.TeamsAccess:
      return TeamsAccessSchema.deserialize(json as TeamsAccessJSON);

    case ResourceType.TeamTemplateAccess:
      return TeamTemplateAccessSchema.deserialize(json as TeamTemplateAccessJSON);

    case ResourceType.ContractsAccess:
      return ContractsAccessSchema.deserialize(json as ContractsAccessJSON);

    case ResourceType.FeeSchedulesAccess:
      return FeeSchedulesAccessSchema.deserialize(json as FeeSchedulesAccessJSON);

    case ResourceType.FeeScheduleTemplateAccess:
      return FeeScheduleTemplateAccessSchema.deserialize(json as FeeScheduleTemplateAccessJSON);

    case ResourceType.CurrencyFieldsAccess:
      return CurrencyFieldsAccessSchema.deserialize(json as CurrencyFieldsAccessJSON);

    case ResourceType.DiscountsAccess:
      return DiscountsAccessSchema.deserialize(json as DiscountsAccessJSON);

    case ResourceType.InvoicesAccess:
      return InvoicesAccessSchema.deserialize(json as InvoicesAccessJSON);

    case ResourceType.ProposalAccess:
      return ProposalAccessSchema.deserialize(json as ProposalAccessJSON);

    case ResourceType.WorkAgreementAccess:
      return WorkAgreementAccessSchema.deserialize(json as WorkAgreementAccessJSON);

    case ResourceType.KnowledgeBaseAccess:
      return KnowledgeBaseAccessSchema.deserialize(json as KnowledgeBaseAccessJSON);

    default:
      // Fallback for unknown resource types - keeps the permission flow resilient.
      return PermissionResourceSchema.deserialize(json);
  }
}

export function serializeResource(params: AnyResourceParams): AnyResourceJSON {
  switch (params.resourceType) {
    case ResourceType.CalendarEventAccess:
      return CalendarEventAccessSchema.serialize(params as CalendarEventAccessParams);

    case ResourceType.GeneralDocumentAccess:
      return GeneralDocumentAccessSchema.serialize(params as GeneralDocumentAccessParams);

    case ResourceType.DocumentContentsAccess:
      return DocumentContentsAccessSchema.serialize(params as DocumentContentsAccessParams);

    case ResourceType.DocumentAccess:
      return DocumentAccessSchema.serialize(params as DocumentAccessParams);

    case ResourceType.LegalEntitiesPlatformAccess:
      return LegalEntitiesPlatformAccessSchema.serialize(params as LegalEntitiesPlatformAccessParams);

    case ResourceType.EntityAgreementAccess:
      return EntityAgreementAccessSchema.serialize(params as EntityAgreementAccessParams);

    case ResourceType.LegalEntityAccess:
      return LegalEntityAccessSchema.serialize(params as LegalEntityAccessParams);

    case ResourceType.CommunicationsAccess:
      return CommunicationsAccessSchema.serialize(params as CommunicationsAccessParams);

    case ResourceType.ForumAccess:
      return ForumAccessSchema.serialize(params as ForumAccessParams);

    case ResourceType.MarketplaceAccess:
      return MarketplaceAccessSchema.serialize(params as MarketplaceAccessParams);

    case ResourceType.MarketplaceSearchAccess:
      return MarketplaceSearchAccessSchema.serialize(params as MarketplaceSearchAccessParams);

    case ResourceType.MarketplaceDirectoryAccess:
      return MarketplaceDirectoryAccessSchema.serialize(params as MarketplaceDirectoryAccessParams);

    case ResourceType.NotificationsAccess:
      return NotificationsAccessSchema.serialize(params as NotificationsAccessParams);

    case ResourceType.LoginAccess:
      return LoginAccessSchema.serialize(params as LoginAccessParams);

    case ResourceType.UserNetworkingAccess:
      return UserNetworkingAccessSchema.serialize(params as UserNetworkingAccessParams);

    case ResourceType.WorkAgreementsAccess:
      return WorkAgreementsAccessSchema.serialize(params as WorkAgreementsAccessParams);

    case ResourceType.ProposalsAccess:
      return ProposalsAccessSchema.serialize(params as ProposalsAccessParams);

    case ResourceType.TeamsAccess:
      return TeamsAccessSchema.serialize(params as TeamsAccessParams);

    case ResourceType.TeamTemplateAccess:
      return TeamTemplateAccessSchema.serialize(params as TeamTemplateAccessParams);

    case ResourceType.ContractsAccess:
      return ContractsAccessSchema.serialize(params as ContractsAccessParams);

    case ResourceType.FeeSchedulesAccess:
      return FeeSchedulesAccessSchema.serialize(params as FeeSchedulesAccessParams);

    case ResourceType.FeeScheduleTemplateAccess:
      return FeeScheduleTemplateAccessSchema.serialize(params as FeeScheduleTemplateAccessParams);

    case ResourceType.CurrencyFieldsAccess:
      return CurrencyFieldsAccessSchema.serialize(params as CurrencyFieldsAccessParams);

    case ResourceType.DiscountsAccess:
      return DiscountsAccessSchema.serialize(params as DiscountsAccessParams);

    case ResourceType.InvoicesAccess:
      return InvoicesAccessSchema.serialize(params as InvoicesAccessParams);

    case ResourceType.ProposalAccess:
      return ProposalAccessSchema.serialize(params as ProposalAccessParams);

    case ResourceType.WorkAgreementAccess:
      return WorkAgreementAccessSchema.serialize(params as WorkAgreementAccessParams);

    case ResourceType.KnowledgeBaseAccess:
      return KnowledgeBaseAccessSchema.serialize(params as KnowledgeBaseAccessParams);
    default:
      return PermissionResourceSchema.serialize(params);
  }
}

export const resourceDeserializers: Record<ResourceType, (json: AnyResourceJSON) => AnyResourceParams> = {
  [ResourceType.CalendarEventAccess]: deserializeResource,
  [ResourceType.GeneralDocumentAccess]: deserializeResource,
  [ResourceType.DocumentContentsAccess]: deserializeResource,
  [ResourceType.DocumentAccess]: deserializeResource,
  [ResourceType.LegalEntitiesPlatformAccess]: deserializeResource,
  [ResourceType.EntityAgreementAccess]: deserializeResource,
  [ResourceType.LegalEntityAccess]: deserializeResource,
  [ResourceType.CommunicationsAccess]: deserializeResource,
  [ResourceType.ForumAccess]: deserializeResource,
  [ResourceType.MarketplaceAccess]: deserializeResource,
  [ResourceType.MarketplaceSearchAccess]: deserializeResource,
  [ResourceType.MarketplaceDirectoryAccess]: deserializeResource,
  [ResourceType.NotificationsAccess]: deserializeResource,
  [ResourceType.LoginAccess]: deserializeResource,
  [ResourceType.UserNetworkingAccess]: deserializeResource,
  [ResourceType.WorkAgreementsAccess]: deserializeResource,
  [ResourceType.ProposalsAccess]: deserializeResource,
  [ResourceType.TeamsAccess]: deserializeResource,
  [ResourceType.TeamTemplateAccess]: deserializeResource,
  [ResourceType.ContractsAccess]: deserializeResource,
  [ResourceType.FeeSchedulesAccess]: deserializeResource,
  [ResourceType.FeeScheduleTemplateAccess]: deserializeResource,
  [ResourceType.CurrencyFieldsAccess]: deserializeResource,
  [ResourceType.DiscountsAccess]: deserializeResource,
  [ResourceType.InvoicesAccess]: deserializeResource,
  [ResourceType.ProposalAccess]: deserializeResource,
  [ResourceType.WorkAgreementAccess]: deserializeResource,
  [ResourceType.KnowledgeBaseAccess]: deserializeResource,
};

export const resourceSerializers: Record<ResourceType, (params: AnyResourceParams) => AnyResourceJSON> = {
  [ResourceType.CalendarEventAccess]: serializeResource,
  [ResourceType.GeneralDocumentAccess]: serializeResource,
  [ResourceType.DocumentContentsAccess]: serializeResource,
  [ResourceType.DocumentAccess]: serializeResource,
  [ResourceType.LegalEntitiesPlatformAccess]: serializeResource,
  [ResourceType.EntityAgreementAccess]: serializeResource,
  [ResourceType.LegalEntityAccess]: serializeResource,
  [ResourceType.CommunicationsAccess]: serializeResource,
  [ResourceType.ForumAccess]: serializeResource,
  [ResourceType.MarketplaceAccess]: serializeResource,
  [ResourceType.MarketplaceSearchAccess]: serializeResource,
  [ResourceType.MarketplaceDirectoryAccess]: serializeResource,
  [ResourceType.NotificationsAccess]: serializeResource,
  [ResourceType.LoginAccess]: serializeResource,
  [ResourceType.UserNetworkingAccess]: serializeResource,
  [ResourceType.WorkAgreementsAccess]: serializeResource,
  [ResourceType.ProposalsAccess]: serializeResource,
  [ResourceType.TeamsAccess]: serializeResource,
  [ResourceType.TeamTemplateAccess]: serializeResource,
  [ResourceType.ContractsAccess]: serializeResource,
  [ResourceType.FeeSchedulesAccess]: serializeResource,
  [ResourceType.FeeScheduleTemplateAccess]: serializeResource,
  [ResourceType.CurrencyFieldsAccess]: serializeResource,
  [ResourceType.DiscountsAccess]: serializeResource,
  [ResourceType.InvoicesAccess]: serializeResource,
  [ResourceType.ProposalAccess]: serializeResource,
  [ResourceType.WorkAgreementAccess]: serializeResource,
  [ResourceType.KnowledgeBaseAccess]: serializeResource,
};
